import {useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import get from 'lodash/get';
import { Box,Chip, FormControlLabel, IconButton,Menu, MenuItem, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import MasterComponent from '../../../common/MasterComponent';
import { machineryManagementActions } from '../../../store/reducers/machinery-management';
import { MachineDataType, MachineryDataRequestPayloadTypes } from '../../../store/models/machine-management';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../utils/table-utils';
import { ReactComponent as FilterIcon } from '../../../assets/icons/main/filter-icon.svg';
import CheckboxComponent from '../../../components/Checkbox';
import { ontologyMasterActions } from '../../../store/reducers/ontology-master';
import { sentenceCase } from 'change-case';
import { palette } from '../../../theme/palette';
import { pageActions } from '../../../store/reducers/page-actions';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';

const selectOptions = [
  {
    label: 'Scheduled',
    value: 'scheduled',
  },
  {
    label: 'Not Scheduled',
    value: 'not_scheduled',
  },
];

const MachineList = ({
  isMachineryManagementDataLoading,
  machineryManagementData,
  machineryManagementDataTotalRowCount,
  machineryManagementDataPageNumber,
  searchItem,
  settingsData,
  ontologyData,
  ontologies
}: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeData: any = useTheme();
  const [machineMasterData, setMachineMasterData] = useState<Array<MachineDataType>>([]);
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const [scheduledStatus, setScheduledStatus] = useState<string[]>([])
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const { pathname, state } = useLocation();

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index != 2)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0], pathName: 'Maintenance Management', navigate: true };
            } else if (index == 1) {
              return { route: '', pathName: 'Machine Maintenance Status', navigate: false };
            } else {
              return { route: '', pathName: item, navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [pathname]);

  useEffect(() => {
    dispatch(ontologyMasterActions.getOntologyList(''));
    dispatch(ontologyMasterActions.getOntologyMgmntList(''));
  }, []);


  const getOntologyData = (ontoId: any) => {
    if (ontologies.length > 0) {
      let tempOntoData: any = [];
      restartTheLoop: while (true) {
        for (const item of ontologies) {
          if (item.onto_manage_id == ontoId) {
            tempOntoData.unshift(item);
            ontoId = item.parent_id;
            continue restartTheLoop;
          }
        }
        break;
      }

      tempOntoData.map((item: any) => {
        let getLevelName = ontologyData.filter((data: any) => {
          return data.id === item.onto_level_number;
        })[0];

        return {
          ...item,
          level_name: getLevelName?.level_name || '',
        };
      });

      return tempOntoData;
    }
    return [];
  };

  useEffect(() => {
    if (machineryManagementData && machineryManagementData.length > 0 && ontologies.length) {
      setMachineMasterData(
        machineryManagementData.map((item: any) => {
          return {
            ...item,
            id: item?.machine_id,
            machine_tags:
              item?.machine_tags && item?.machine_tags.length > 0
                ? item?.machine_tags.map((val: any, index: any) => {
                  return { ...val, id: index + 1 };
                })
                : [],
            ontologyList: getOntologyData(item.onto_manage_id),
          };
        })
      );
    } else {
      setMachineMasterData([]);
    }
  }, [machineryManagementData, ontologies]);

  const pageData: any = get(useOutletContext(), 'outletData', {});
  var pluralize = require('pluralize');

  const dispatchMachineryDataRequest: any = (payload: MachineryDataRequestPayloadTypes) => {
    dispatch(
      machineryManagementActions.getMachineryManagementDataRequest({
        ...sortingModel,
        search_item: searchItem,
        ...payload,
        pageName: get(pageData, 'entity_name', 'Machine'),
      })
    );
  };

  useEffect(() => {
    dispatchMachineryDataRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER, onto_manage_id: get(state, 'selectedOntoId', 0) });
    return () => {
      dispatch(machineryManagementActions.resetLoadingState());
      dispatch(machineryManagementActions.resetMachineryManagementData());
    };
  }, [state, settingsData]);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRowSelection = (params: any) => {
    setSelectedRows(params);
  };

  const handleOptionChange = (option: string) => {
    const status = scheduledStatus.includes(option) ? scheduledStatus.filter((status: string) => status !== option) : [...scheduledStatus, option]
    setScheduledStatus(status)
    dispatchMachineryDataRequest({
      limit: DEFAULT_PAGE_SIZE,
      page: DEFAULT_PAGE_NUMBER,
      maintenance_status: status.join(',')
    });
  }

  const columns: any = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      width: 70,
    },
    {
      field: 'machine_name',
      headerName: `${get(pageData, 'entity_name', 'Machine')}`,
      flex: 1,
      minWidth: 300,
      renderCell: (params: any) => {
        return (
          <div style={{ cursor: 'pointer' }}>
            <Tooltip title={params.value} placement='left' arrow>
              <Typography
                component='p'
                variant='subtitle1'
                sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {params?.value} <br />
              </Typography>
            </Tooltip>
          </div>
        );
      },
      sortComparator: (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }),
    },
    {
      field: 'onto_manage_id',
      headerName: 'Location',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 400,
      renderCell: (params: any) => {
        const ontoList =
          params.row.ontologyList && params.row.ontologyList.length
            ? params.row.ontologyList.map((item: any, index: number) =>
              params.row.ontologyList.length > 1 ? (
                index === params.row.ontologyList.length - 1 ? (
                  <span>{item.onto_manage_title} </span>
                ) : (
                  <span>{item.onto_manage_title} / </span>
                )
              ) : (
                <span>{item.onto_manage_title}</span>
              )
            )
            : '';

        return (
          <Box sx={{ textWrap: 'wrap', lineHeight: '1.2', width: '400px' }}>
            <Tooltip title={ontoList} placement='left' arrow>
              <Typography
                component='p'
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: '16px !important',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {ontoList}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'maintenance_status',
      headerName: 'Maintenance',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      minWidth: 200,
      renderCell: (params: any) => {
        return (
          <>
            <Chip
              label={sentenceCase(params.value || 'not_scheduled')}
              className={params.value === 'scheduled' ? 'scheduled' : 'not_scheduled'}
              variant='outlined'
              sx={{padding:'0px 8px 0px 8px!important',  width:'160px'}}
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <MasterComponent
        heading={
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '12px' }}>
            <Typography variant='h2'
              color={themeData.palette.text.primary}
            >
              {`List of ${pluralize(get(pageData, 'entity_name', 'Machine'))}`}
            </Typography>
            <IconButton onClick={handleClick} sx={{ p: 0 }}>
              <FilterIcon style={{ color: themeData.palette.custom.light }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                top: '40px',
                left:'-10px',
                '.MuiMenu-paper': {
                  backgroundColor: palette.primary.contrastText,
                  boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px 8px 8px 8px',
                  border: `1px solid ${themeData.palette.customBorderColor.main}`,
                  // borderTop: 'none',
                  // padding: '10px 0',

                },
              }}
            >
              {selectOptions.map((option) => (
                <MenuItem key={option.value}>
                  <FormControlLabel
                    control=
                    {<CheckboxComponent checked={scheduledStatus.includes(option.value)} handleCheckboxChange={() => handleOptionChange(option.value)} />}
                    label={<Typography variant='subtitle1'>{option.label}</Typography>}
                    sx={{ margin: 0 }}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>}
        addNewLabel='Add Maintenance'
        onAddNewClick={() => {
          navigate('/maintenance-management/plan-list/add', {
            state: {
              machines: selectedRows,
              from: 'machine-list'
            },
          });
        }}
        isLoading={isMachineryManagementDataLoading}
        rows={machineMasterData}
        columns={columns}
        isServerPagination
        checkboxSelection
        onRowSelectionChange={handleRowSelection}
        isRowSelectable={() => true}
        selectionModel={selectedRows}
        totalRowCount={machineryManagementDataTotalRowCount}
        pageNumber={machineryManagementDataPageNumber}
        onPagination={(pageSize: number, pageNumber: number) => {
          dispatchMachineryDataRequest({
            limit: pageSize,
            page: pageNumber,
          });
        }}
        onSorting={(sortModel: { field: string; sort: string }) => {
          dispatchMachineryDataRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: machineryManagementDataPageNumber,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        isTemplate={true}
        dataColumns={['machine_name', 'location', 'maintenance_status']}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            {get(pageData, 'entity_name', 'Machine')} data not available
          </Stack>
        )}
        showSearch
        searchPlaceholder='Search'
        onSearch={(searchValue: string) => {
          dispatchMachineryDataRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            search_item: searchValue,
          });
          dispatch(machineryManagementActions.onSearchForItem(searchValue));
        }}
      />

    </>
  );
};

const mapStateToProps = (state: any) => ({
  isMachineryManagementDataLoading: state.machineryManagement.isMachineryManagementDataLoading,
  machineryManagementData: state.machineryManagement.machineryManagementData,
  machineryManagementDataTotalRowCount: state.machineryManagement.machineryManagementDataTotalRowCount,
  machineryManagementDataPageNumber: state.machineryManagement.machineryManagementDataPageNumber,
  searchItem: state.machineryManagement.searchItem,
  settingsData: state.settings.settingsData,
  isOntoManagesLoading: state.ontologyMaster.isOntoManagesLoading,
  ontologyData: state.ontologyMaster.ontologyData,
  ontologies: state.ontologyMaster.ontologyMgmntData,
});

export default connect(mapStateToProps)(MachineList);
